import { Button, Col, Form, Input, message, Modal, Row, Select, UploadProps } from 'antd';
import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { AppDispatch } from '../../../../store/store';

import TextArea from 'antd/es/input/TextArea';
import Dragger from 'antd/es/upload/Dragger';
import { InboxOutlined, LoadingOutlined } from '@ant-design/icons';
import SearchAgreementResult from '../../../AccountEnquiry/SearchAgreementResult';
import { searchDisbursedAgreementThunk } from '../../../../services/agreementService/agreementThunk';
import { collectionStateReset } from '../../../../features/collection/collectionSlice';
import LoadingSpinner from '../../../../components/LoadingSpinner';
import { createRefinanceThunk } from '../../../../services/refinanceService/refinanceThunk';
import { RcFile } from 'antd/es/upload';

const { Search } = Input;

type NewRefinanceRequestModalProps = {
  successCallback?: any;
};

const NewRefinanceRequestModal: React.FC<NewRefinanceRequestModalProps> = (props) => {
  const { successCallback } = props;
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const pageNum = queryParams.get('pageNum');
  const pageSize = queryParams.get('pageSize');

  const initialParams = useMemo(
    () => ({
      pagination: {
        current: pageNum ? +pageNum : 1,
        pageSize: pageSize ? +pageSize : 20,
      },
      sortOrder: 'desc',
      sortField: 'submissionDate',
      search: {},
      filters: {},
    }),
    [pageNum, pageSize]
  );

  const [paginationState, setPaginationState] = useState(initialParams.pagination);
  const [searchType, setSearchType] = useState('agreementNo');
  const [fileList, setFileList] = useState<RcFile[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [searchResultVisible, setSearchResultVisible] = useState(false);
  const [accountDetailsSearchResults, setAccountDetailsSearchResults] = useState<any>([]);
  const [selectedAccountDetails, setSelectedAccountDetails] = useState<any>(null);
  const [remarks, setRemarks] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const uploadProps: UploadProps<RcFile> = {
    multiple: true,
    onRemove: (file) => {
      const index = fileList.indexOf(file as RcFile); //~ Idk why the file param is RcFile instead of UploadFile<RcFile>. Probably a Dragger bug
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
    beforeUpload: (file) => {
      fileList.push(file);
      return false;
    },
    fileList,
  };

  const searchTypes = [
    { value: 'agreementNo', label: 'Agreement No' },
    { value: 'applicantNric', label: 'Applicant NRIC' },
    { value: 'applicantFirstName', label: 'Applicant Name' },
    { value: 'applicantPhoneNo', label: 'Phone No' },
    { value: 'applicantEmail', label: 'Email' },
    { value: 'staffId', label: 'Staff ID' },
    { value: 'applicantPartnerId', label: 'Partner ID' },
  ];

  const showModal = () => {
    setIsModalOpen(true);
  };
  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const onSearch = async (value: string) => {
    const trimmedValue = value.trim();

    if (trimmedValue) {
      setLoading(true);
      setSearchValue(trimmedValue);

      await dispatch(searchDisbursedAgreementThunk({ [searchType]: trimmedValue }))
        .unwrap()
        .then((result) => {
          setAccountDetailsSearchResults(result);
          if (result && result.length > 0) {
            setSearchResultVisible(true);
          } else {
            message.error('Result Not Found');
          }
        })
        .catch((error) => {
          console.error('[onSearch] Error during search:', error);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      message.error('Result Not Found');
      dispatch(collectionStateReset());
    }
  };

  const handleSearchResultSelect = (data: any) => {
    const agreement = accountDetailsSearchResults.find(
      (agreement: any) => agreement.agreementNo === data
    );
    setSelectedAccountDetails(agreement);
    setSearchResultVisible(false);
  };

  const handleSearchResultCancel = () => {
    setSearchResultVisible(false);
  };

  const createNewRefinance = async () => {
    if (!selectedAccountDetails) {
      message.error('Please select an agreement');
      return;
    } else if (fileList.length === 0) {
      message.error('Please upload supporting documents');
      return;
    } else if (remarks?.trim() === '') {
      message.error('Please enter remarks');
      return;
    }
    setCreating(true);
    const payload = new FormData();
    payload.append('agreementNo', selectedAccountDetails.agreementNo);
    payload.append('remarks', remarks);
    fileList.forEach((file) => {
      payload.append('files', file);
    });
    await dispatch(createRefinanceThunk(payload))
      .unwrap()
      .then((res) => {
        message.success('Refinance request successfully created');
        setFileList([]);
        setRemarks('');
        setSelectedAccountDetails(null);
        setIsModalOpen(false);
        successCallback(res);
      })
      .catch((error) => {
        console.error('[createNewRefinance] Error:', error);
      })
      .finally(() => {
        setCreating(false);
      });
  };

  return (
    <>
      <Button onClick={() => showModal()} type="primary">
        New Request
      </Button>
      <Modal
        open={isModalOpen}
        onOk={createNewRefinance}
        onCancel={handleCancel}
        closable={false}
        width={600}
        footer={null}
      >
        {loading ? (
          <LoadingSpinner />
        ) : (
          <>
            <Form layout="vertical" className="p-4">
              <Input.Group compact className="w-full flex">
                <Col>
                  <Select
                    defaultValue={'agreementNo'}
                    style={{ width: 200 }}
                    onChange={(value) => {
                      // setResultNotFound(false);
                      setSearchType(value);
                    }}
                    options={searchTypes}
                  />
                </Col>
                <Col>
                  <Search
                    placeholder={searchTypes.find((item) => item.value === searchType)?.label}
                    onSearch={onSearch}
                    defaultValue={searchValue}
                  />
                </Col>
              </Input.Group>
              <br />

              <Form.Item label="Customer Name">
                <Input
                  disabled
                  value={selectedAccountDetails?.application?.applicantFirstName}
                ></Input>
              </Form.Item>

              <Form.Item label="Supporting Documents">
                <Dragger {...uploadProps}>
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">Click or drag file to this area to upload</p>
                </Dragger>
              </Form.Item>

              <br />
              <Form.Item label="Remark">
                <TextArea
                  rows={4}
                  placeholder="Remarks"
                  onChange={(e) => setRemarks(e.target.value)}
                />
              </Form.Item>
            </Form>
            <Row justify="end">
              <Col>
                <Button onClick={handleCancel} disabled={creating}>
                  Cancel
                </Button>
              </Col>
              <Col>
                <Button
                  type="primary"
                  htmlType="submit"
                  onClick={createNewRefinance}
                  loading={creating}
                >
                  OK
                </Button>
              </Col>
            </Row>
          </>
        )}
      </Modal>
      {searchResultVisible && (
        <SearchAgreementResult
          visible={searchResultVisible}
          data={accountDetailsSearchResults}
          onOk={handleSearchResultSelect}
          onCancel={handleSearchResultCancel}
        />
      )}
    </>
  );
};

export default NewRefinanceRequestModal;
